import { Box, Drawer, Grid, Typography } from "@mui/material";
import { useAchievements } from "api/achievements";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useAppSelector } from "app/hooks";
import { AdvancedSettingsBar } from "components/Campaign/CampaignEffects/AdvancedSettingsBar/AdvancedSettingsBar";
import { InlineInput } from "components/FormControls/InlineInput/InlineInput";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { GoalDescription } from "components/Layout/Achievement/AchievementForm/Rules/RuleGoal/RuleGoalReadOnly";
import { AchievementAvailability } from "components/Layout/Achievement/AchievementForm/Summary/helpers";
import { parseToFEPeriod } from "components/Layout/Achievement/helpers";
import { BarChartPercentage } from "components/Table/BarChartPercentage";
import { StatusValue } from "components/Table/StatusValue";
import { OLTabs } from "components/Tabs/OLTabs";
import { ExternalLinkTooltip } from "components/Tooltips/ExternalLinkTooltip";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { ExternalLink } from "ExternalLink";
import { FieldArray, Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useIsOverflown } from "hooks/useIsOverflown";
import { get, isEmpty } from "lodash";
import { OLButton } from "OLButton";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import {
  AchievementCompleteRulePeriod,
  AchievementTriggers,
} from "types/achievements";
import { SelectedLimitType } from "types/campaigns";
import { MemberAchievementProgress } from "types/members";
import { backendErrors } from "utilities/backendErrors";
import { OLDate } from "utilities/dateFormat";
import { focusError } from "utilities/focusError";
import { formatNumberValue } from "utilities/numbers";
import { useStyles } from "./AchievementDetailsDrawer.styles";
import { ConsecutiveRuleTooltip } from "./ConsecutiveRuleTooltip";
import { TriggeredCustomEvents } from "./TriggeredCustomEvents";
import { TriggeredTransactions } from "./TriggeredTransactions";

export function AchievementDetailsDrawer({
  achievementPogressDetails,
  achievementId,
  open,
  close,
}: {
  achievementPogressDetails: MemberAchievementProgress;
  achievementId: string;
  open: boolean;
  close: () => void;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { id: memberID } = useParams<{ id: string }>();
  const { observeElement, isOverflown } = useIsOverflown();
  const { useAchievementData, manageProgress } = useAchievements();
  const triggersSectionRef = useRef(null);
  const { data: achievementData } = useAchievementData(achievementId);
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRule, setSelectedRule] = useState<any>();
  const [isCompletedCountEditing, setIsCompletedCountEditing] = useState(false);
  const [isRuleProgressEditing, setIsRuleProgressEditing] = useState(false);

  const { OVERALL } = AchievementCompleteRulePeriod;
  const achievementName = achievementData?.translations[defaultLang.code].name;
  const achievementDescription =
    achievementData?.translations[defaultLang.code].description;
  const achievementRulesNumber = achievementData?.rules?.length || 0;
  const defaultRuleName = message(
    { id: "ruleNumber" },
    { number: activeTab + 1 },
  );
  const isRuleConsecutive = selectedRule?.consecutivePeriods !== undefined;
  const isRuleOverall = selectedRule?.periodType === OVERALL;
  const selectedRuleLimitData =
    selectedRule &&
    achievementData?.rules &&
    achievementData.rules[activeTab].limit;
  const editedByAdminDate = get(achievementPogressDetails, "editedByAdminAt");
  const achievementLimit =
    achievementData?.limit && (achievementData?.limit as SelectedLimitType);

  const initialValues = achievementPogressDetails && {
    completedCount:
      achievementPogressDetails?.memberProgress.completedCount.toString(),
    rules: achievementPogressDetails?.memberProgress.rules.map((rule) => ({
      ...rule,
      currentPeriodValue: rule.currentPeriodValue.toString(),
    })),
  };
  const isBarChartVisible =
    !!selectedRule?.periodGoal && !isRuleProgressEditing;

  const getEventFrequencyLimitValue = () => {
    if (selectedRuleLimitData?.hasOwnProperty("interval")) {
      let intervalType;
      switch (selectedRuleLimitData?.interval?.type) {
        case "calendarHours":
          intervalType = message({ id: "perHour" });
          break;
        case "calendarDays":
          intervalType = message({ id: "perDay" });
          break;
        case "calendarWeeks":
          intervalType = message({ id: "perWeek" });
          break;
        case "calendarMonths":
          intervalType = message({ id: "perMonth" });
          break;
        case "calendarYears":
          intervalType = message({ id: "perYear" });
          break;
        default:
          intervalType = "";
          break;
      }
      return (
        <>
          <span className={classes.bold}>
            {formatNumberValue(selectedRuleLimitData?.interval?.value)}
          </span>
          /{formatNumberValue(selectedRuleLimitData?.value)}{" "}
          {intervalType.toLocaleLowerCase()}
        </>
      );
    } else {
      return message({ id: "unlimited" });
    }
  };

  const getCompletedCountValue = (completedCount: string) => {
    const formattedCompletedCount = (
      <span className={classes.bold}>
        {formatNumberValue(completedCount?.toString(), "0")}
      </span>
    );

    return isEmpty(achievementData?.limit) ? (
      formattedCompletedCount
    ) : (
      <>
        {formattedCompletedCount}/
        {formatNumberValue(achievementLimit?.value, "0")}{" "}
        {message({ id: "inTotal" }).toLocaleLowerCase()}
      </>
    );
  };

  const scrollTriggersSection = () => {
    if (triggersSectionRef && triggersSectionRef.current) {
      (triggersSectionRef.current as HTMLElement).scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const achievementDataSelectedRule =
    achievementData &&
    achievementData.rules.find(
      (rule) => rule?.achievementRuleId === selectedRule?.achievementRuleId,
    );

  const customEventTriggerLabels = achievementDataSelectedRule && [
    {
      label: message({ id: "countUniqueCustomEventAttributes" }),
      value: "",
    },
    {
      label: message({ id: "customEventAttribute" }),
      value: achievementDataSelectedRule.completeRule?.uniqueAttribute || "",
    },
  ];

  useEffect(() => {
    const ruleData = achievementPogressDetails?.memberProgress.rules[activeTab];
    setSelectedRule(ruleData);
  }, [activeTab, achievementPogressDetails?.memberProgress.rules]);

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={close}
      classes={{ paper: classes.drawer }}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});

          // Editing achievement progression currently only applies to overall rules
          const filteredRules = values.rules
            .filter((rule) => rule.periodType === OVERALL)
            .map((rule) => ({
              achievementRuleId: rule.achievementRuleId as string,
              currentPeriodValue: Number(rule.currentPeriodValue),
              completedConsecutivePeriods: rule.completedConsecutivePeriods
                ? Number(rule.completedConsecutivePeriods)
                : undefined,
            }));

          try {
            const response = await manageProgress(
              achievementPogressDetails.achievementId,
              memberID as string,
              {
                completedCount: Number(values.completedCount),
                rules: filteredRules,
              },
            );
            if (response) {
              notify(message({ id: "progressUpdated" }));
              close();
            }
          } catch (exception) {
            notify(getErrorMessage(exception));

            const errors = backendErrors(getBackendErrors(exception));
            const ruleIndexes = filteredRules.map((item) =>
              values.rules.findIndex(
                (rule) => item.achievementRuleId === rule.achievementRuleId,
              ),
            );
            const result = {
              ...errors,
              rules: errors?.rules
                ? Object.fromEntries(
                    Object.entries(errors?.rules)?.map(([key, value]) => [
                      ruleIndexes[key as any],
                      value,
                    ]),
                  )
                : undefined,
            };

            focusError();
            setStatus(result);
          }
        }}
      >
        {({
          isSubmitting,
          values,
          status,
          dirty,
          handleSubmit,
          setValues,
          setStatus,
        }) => {
          const completedCountError = get(status, "completedCount");

          const tabs = [...Array(achievementRulesNumber)].map((_, index) => {
            const ruleData =
              achievementPogressDetails?.memberProgress.rules[index];
            const ruleTranslation = ruleData?.translations?.[defaultLang?.code];
            const rulesError = get(status, "rules");
            const ruleHasError = rulesError && rulesError[index];
            const ruleName = ruleTranslation?.name
              ? ruleTranslation.name
              : message({ id: "ruleNumber" }, { number: index + 1 });

            return {
              id: index.toString(),
              name: ruleName,
              isError: ruleHasError,
            };
          });

          return (
            <Form onSubmit={handleSubmit} className={classes.form}>
              <div className={classes.header}>
                <Typography className={classes.title}>
                  {message({ id: "achievementDetails" })}
                </Typography>
                {achievementData && (
                  <StatusValue status={!!achievementData?.active} />
                )}
                <XCloseIcon
                  onClick={close}
                  className={classes.closeIcon}
                  data-test-id="close-achievement-drawer"
                />
              </div>
              <div className={classes.content}>
                <Typography
                  mb={2}
                  ref={observeElement}
                  className={classes.title}
                >
                  <Tooltip
                    text={achievementName}
                    disableHoverListener={!isOverflown}
                    placement="bottom-start"
                  >
                    <span>{achievementName}</span>
                  </Tooltip>
                </Typography>
                <Typography fontSize={14}>{achievementDescription}</Typography>
                <Grid container className={classes.detailsBox} mt={12}>
                  {editedByAdminDate && (
                    <>
                      <Grid item xs={5}>
                        <Typography
                          className={classes.subtitle}
                          maxWidth={130}
                          mb={16}
                          data-test-id="manually-changed-progress-label"
                        >
                          {message({ id: "manuallyChangedProgress" })}
                        </Typography>
                      </Grid>
                      <Grid item xs={7} mb={16}>
                        <div className={classes.progressWrapper}>
                          <AlertTriangleIcon
                            className={classes.orangeAlertTriangleIcon}
                          />
                          {OLDate(editedByAdminDate)}{" "}
                        </div>
                      </Grid>
                    </>
                  )}
                  {isCompletedCountEditing && (
                    <Grid item xs={12} mb={8}>
                      <div className={classes.manageProgressInfo}>
                        <span>
                          {message(
                            {
                              id: "manageProgressInfo",
                            },
                            {
                              seeExample: (
                                <Tooltip
                                  text={
                                    <div className={classes.tooltipText}>
                                      {message({
                                        id: "manageProgressInfoTooltip",
                                      })}
                                    </div>
                                  }
                                >
                                  <span className={classes.highlighted}>
                                    {message({
                                      id: "seeTheExample",
                                    })}
                                  </span>
                                </Tooltip>
                              ),
                              learnMore: (
                                <ExternalLink path="https://help.openloyalty.io/main-features/members/member-list/editing-members#manual-achievement-progress-change">
                                  {message({ id: "learnMore" }).toLowerCase()}
                                </ExternalLink>
                              ),
                            },
                          )}
                        </span>
                      </div>
                    </Grid>
                  )}
                  <Grid item xs={5} alignSelf="center">
                    <Typography className={classes.subtitle}>
                      {message({ id: "completionStatus" })}
                    </Typography>
                  </Grid>
                  <Grid item xs={7} display="flex">
                    {!isCompletedCountEditing && (
                      <Typography>
                        {getCompletedCountValue(
                          values?.completedCount?.toString(),
                        )}
                      </Typography>
                    )}
                    <InlineInput
                      autoFocus
                      type="number"
                      hideLabel={true}
                      name="completedCount"
                      options={{
                        disablePreviewTooltip: true,
                      }}
                      data-test-id="completion-count"
                      onEditModeOpen={() => setIsCompletedCountEditing(true)}
                      onEditModeClose={() => setIsCompletedCountEditing(false)}
                    />
                  </Grid>
                  {!!completedCountError && (
                    <span className={classes.error}>{completedCountError}</span>
                  )}
                </Grid>
                <div className={classes.tabsWrapper}>
                  {achievementRulesNumber > 1 && (
                    <OLTabs
                      items={tabs}
                      onChange={(_, newValue: string) => {
                        setActiveTab(Number(newValue));
                      }}
                      value={activeTab.toString()}
                      maxLabelLength={12}
                    />
                  )}
                </div>
                <FieldArray
                  name="rules"
                  render={() =>
                    values?.rules?.map((_, index: number) => {
                      const isRuleActive = activeTab === index;

                      const ruleError = get(
                        status,
                        `rules.${activeTab}.currentPeriodValue`,
                      );

                      const currentPeriodValue = values.rules.find(
                        (rule) =>
                          rule.achievementRuleId ===
                          selectedRule?.achievementRuleId,
                      )?.currentPeriodValue;

                      return (
                        isRuleActive && (
                          <Grid
                            container
                            key={index}
                            className={`${classes.detailsBox}`}
                          >
                            <Grid item xs={5} mb={16}>
                              <Typography
                                className={classes.subtitle}
                                data-test-id="rule-name-label"
                              >
                                {message({ id: "ruleName" })}
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={7}
                              mb={16}
                              className={classes.breakWord}
                            >
                              {selectedRule?.translations?.[defaultLang.code]
                                .name || defaultRuleName}
                            </Grid>
                            <Grid item xs={5} mb={16}>
                              <Typography
                                className={classes.subtitle}
                                data-test-id="rule-description-label"
                              >
                                {message({ id: "ruleDescription" })}
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={7}
                              mb={16}
                              className={classes.breakWord}
                            >
                              {selectedRule?.translations?.[defaultLang.code]
                                .description || "-"}
                            </Grid>
                            <Grid item xs={5} mb={16}>
                              <Typography
                                className={classes.subtitle}
                                data-test-id="timeframe-label"
                              >
                                {message({ id: "timeframe" })}
                              </Typography>
                            </Grid>
                            <Grid item xs={7} mb={16}>
                              <AchievementAvailability
                                {...achievementData?.activity}
                              />
                            </Grid>
                            <Grid item xs={5} mb={16}>
                              <Typography
                                className={classes.subtitle}
                                data-test-id="achievement-type-label"
                              >
                                {message({ id: "achievementType" })}
                              </Typography>
                            </Grid>
                            <Grid item xs={7} mb={16}>
                              {message({ id: selectedRule?.type })}
                            </Grid>
                            <Grid item xs={5} mb={16}>
                              <Typography
                                className={classes.subtitle}
                                data-test-id="achievement-trigger-label"
                              >
                                {message({ id: "achievementTrigger" })}
                              </Typography>
                            </Grid>
                            <Grid
                              className={classes.linkWrapper}
                              item
                              mb={16}
                              xs={7}
                            >
                              {message({
                                id:
                                  selectedRule?.trigger ===
                                  AchievementTriggers.CUSTOM_EVENT
                                    ? "customEvent"
                                    : selectedRule?.trigger,
                              })}
                              <Typography
                                className={classes.highlighted}
                                data-test-id="see-triggers-history"
                                onClick={scrollTriggersSection}
                              >
                                {message({ id: "seeTriggersHistory" })}
                              </Typography>
                            </Grid>
                            <Grid item xs={5} mb={16}>
                              <Typography
                                className={classes.subtitle}
                                data-test-id="achievement-event-frequency-limit-label"
                              >
                                {message({ id: "eventFrequencyLimit" })}
                              </Typography>
                            </Grid>
                            <Grid item xs={7} mb={16}>
                              {getEventFrequencyLimitValue()}
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                data-test-id="goal-label"
                                className={classes.subtitle}
                              >
                                <span className={classes.subtitleWithIcon}>
                                  {message({ id: "goal" })}
                                  <ExternalLinkTooltip
                                    name="goal"
                                    text={message({ id: "goalInfo" })}
                                  />
                                </span>
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              className={classes.goalDescription}
                            >
                              <GoalDescription
                                completeRule={{
                                  periodGoal: selectedRule?.periodGoal,
                                  period: parseToFEPeriod({
                                    consecutive:
                                      selectedRule?.consecutivePeriods,
                                    type: selectedRule?.periodType,
                                    value: selectedRule?.periodValue,
                                  }),
                                }}
                                trigger={selectedRule?.trigger}
                                event={achievementDataSelectedRule?.event}
                                aggregation={
                                  achievementDataSelectedRule?.aggregation
                                }
                              />
                              {!!achievementDataSelectedRule?.completeRule
                                ?.uniqueAttribute && (
                                <>
                                  <Box mt={10} />
                                  <AdvancedSettingsBar
                                    variant="text"
                                    labels={customEventTriggerLabels || []}
                                    vectorMarginClass={classes.vectorMargin}
                                    advancedSettingsClass={
                                      classes.goalAdvancedSettings
                                    }
                                  />
                                </>
                              )}
                            </Grid>
                            <Grid item xs={5} alignSelf="center">
                              <Typography
                                className={classes.subtitle}
                                data-test-id="current-progress-label"
                              >
                                <span className={classes.subtitleWithIcon}>
                                  {message({ id: "currentProgress" })}
                                  <ExternalLinkTooltip
                                    externalLink="https://help.openloyalty.io/main-features/members/member-list#achievement-status"
                                    name="currentProgress"
                                    text={message({
                                      id: "currentProgressInfo",
                                    })}
                                  />
                                </span>
                              </Typography>
                            </Grid>
                            <Grid item xs={7} display="flex">
                              {isBarChartVisible && (
                                <BarChartPercentage
                                  barChartData={[
                                    {
                                      itemShare: Number(currentPeriodValue),
                                    },
                                    {
                                      itemShare:
                                        selectedRule?.periodGoal -
                                        Number(currentPeriodValue),
                                    },
                                  ]}
                                  className={classes.barChart}
                                />
                              )}
                              {!isRuleProgressEditing && (
                                <>
                                  <Box
                                    sx={{ fontWeight: "bold" }}
                                    data-test-id="current-progress-current"
                                  >
                                    {formatNumberValue(currentPeriodValue, "0")}
                                  </Box>
                                  /
                                  <span data-test-id="current-progress-target">
                                    {formatNumberValue(
                                      selectedRule?.periodGoal,
                                    )}
                                  </span>
                                </>
                              )}
                              {isRuleOverall && (
                                <InlineInput
                                  autoFocus
                                  type="number"
                                  options={{
                                    disablePreviewTooltip: true,
                                  }}
                                  hideLabel={true}
                                  onEditModeOpen={() =>
                                    setIsRuleProgressEditing(true)
                                  }
                                  onEditModeClose={() =>
                                    setIsRuleProgressEditing(false)
                                  }
                                  name={`rules[${activeTab}].currentPeriodValue`}
                                  data-test-id={`current-period-value-${index + 1}`}
                                />
                              )}
                            </Grid>
                            {!!ruleError && (
                              <span className={classes.error}>{ruleError}</span>
                            )}
                            {isRuleConsecutive && (
                              <>
                                <Grid item xs={5} mt={16}>
                                  <Typography
                                    className={classes.subtitle}
                                    data-test-id="completed-periods-label"
                                  >
                                    <span className={classes.subtitleWithIcon}>
                                      {message({ id: "completedPeriods" })}
                                      <ConsecutiveRuleTooltip />
                                    </span>
                                  </Typography>
                                </Grid>
                                <Grid item xs={7} display="flex" mt={16}>
                                  <BarChartPercentage
                                    className={classes.barChart}
                                    barChartData={[
                                      {
                                        itemShare:
                                          selectedRule?.completedConsecutivePeriods,
                                      },
                                      {
                                        itemShare:
                                          selectedRule?.consecutivePeriods,
                                      },
                                    ]}
                                  />
                                  <Box
                                    sx={{ fontWeight: "bold" }}
                                    data-test-id="completed-periods-current"
                                  >
                                    {formatNumberValue(
                                      selectedRule?.completedConsecutivePeriods,
                                    )}
                                  </Box>
                                  /
                                  <span data-test-id="completed-periods-target">
                                    {formatNumberValue(
                                      selectedRule?.consecutivePeriods,
                                    )}
                                  </span>
                                </Grid>
                              </>
                            )}
                          </Grid>
                        )
                      );
                    })
                  }
                />
                {memberID &&
                  selectedRule &&
                  selectedRule.achievementRuleId &&
                  selectedRule.trigger && (
                    <Box ref={triggersSectionRef}>
                      {selectedRule?.trigger ===
                      AchievementTriggers.TRANSACTION ? (
                        <TriggeredTransactions
                          achievementRuleId={selectedRule?.achievementRuleId}
                          memberID={memberID}
                        />
                      ) : selectedRule?.trigger ===
                        AchievementTriggers.CUSTOM_EVENT ? (
                        <TriggeredCustomEvents
                          achievementRuleId={selectedRule?.achievementRuleId}
                          memberID={memberID}
                        />
                      ) : null}
                    </Box>
                  )}
              </div>
              <Box className={classes.btnContainer}>
                <OLButton
                  variant="outlined"
                  onClick={() => {
                    if (dirty) {
                      setStatus({});
                      setValues(initialValues);
                    } else {
                      close();
                    }
                  }}
                  data-test-id="close-drawer"
                >
                  {message({ id: dirty ? "cancel" : "close" })}
                </OLButton>
                {dirty && (
                  <OLButton
                    type="submit"
                    variant="contained"
                    data-test-id="save-achievement"
                    className={classes.saveButton}
                    disabled={isSubmitting}
                  >
                    {message({ id: "saveChanges" })}
                  </OLButton>
                )}
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
}
